import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

import * as PositionManager from "Utils/PositionManager";

import { TrashIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon } from "@heroicons/react/outline";

import Button from "UI/Button";
import Feed, { FeedItem } from "UI/Feed";
import Modal from "UI/Modal";
import TextArea from "UI/Form/TextArea";
import TrayButton from "UI/Form/TextArea/TrayButton";
import ErrorBoundary from "Components/ErrorBoundary";
import { tweetValidation } from "Data/posts/PostValidators";

import AttachedImages from '../AttachedImages';

const defaultState = ({post}) => ({
    busy: false,
    post: post,
    message: post.message,
    replies: post.replies,
    deleted: [],
    validation: {
        hasErrors: false,
        message: {},
        replies: []
    },
    autoFocus: null
});

export default class TwitterPostEditor extends React.Component
{
    static propTypes = {
        open: PropTypes.bool.isRequired,
        onClose: PropTypes.func.isRequired,

        post: PropTypes.object.isRequired,
        media: PropTypes.array.isRequired,

        editPost: PropTypes.func.isRequired,
        removeImage: PropTypes.func.isRequired,
        editPostReply: PropTypes.func.isRequired,
        editPostReplies: PropTypes.func.isRequired,
        removePostReply: PropTypes.func.isRequired,
        removePostReplies: PropTypes.func.isRequired,
    };

    constructor(props)
    {
        super(props);

        this.state = defaultState(props);

        this.runValidation();
    }

    runValidation(specificTweet) {

        const validateTweet = (tweet) => {
            tweetValidation.reset();
            const result = tweetValidation(tweet);

            return {
                hasErrors: result.hasErrors('tweet'),
                help: result.getErrors('tweet').join(' ')
            }
        };

        if (specificTweet) {
            return validateTweet(specificTweet);
        }

        let validation = {
            message: validateTweet({message: this.state.post.message}),
            replies: this.state.replies.map(reply => validateTweet(reply)),
            hasErrors: false
        };

        if (validation.message.hasErrors) {
            validation.hasErrors = true;
        }

        validation.replies.map(reply => {
            if (reply.hasErrors) {
                validation.hasErrors = true
            }

            return reply;
        })

        this.setState({
            validation: {
                ...validation
            }
        });
    }

    onSubmit = (e) => {
        e.preventDefault();

        // todo... run all the validations...?
        // check the hasErrors()
        // prevent if errors..
        // heck... we should disable the button if there are errors...
        // the button is now disabled... but we probably should do an extra check...
        // also - @todo... check the validation on the back-end...
        // and  - @todo... don't do any then() if we had a validation error and present error messages

        this.setState({busy: true}, () => {
            Promise.all([

                this.props.removePostReplies(this.props.post.id, this.state.deleted),
                this.props.editPostReplies(this.props.post.id, this.state.replies),
                this.props.editPost(this.props.post.id, this.state.post.message, this.state.post.media),

            ]).then(() => {
                this.setState({busy: false}, this.props.onClose);
            }).catch(() => {
                this.setState({busy: false});
            });
        });

    };

    onDelete = (replyId) => {

        // Shift the deleted one to the end - quick way to adjust all other positions.
        const orderedReplies = PositionManager.move(this.state.replies, replyId, this.state.replies.length + 1)

        this.setState({
            replies: orderedReplies.filter(reply => reply.id !== replyId),
            deleted: [
                ...this.state.deleted,
                replyId
            ]
        }, () => {
            this.runValidation();
        })
    };

    onMessageChanged = (e) => {
        const post = this.state.post;
        post.message = e.currentTarget.value;

        this.setState({
            ...this.state,
            post: post
        }, () => {
            this.runValidation();
        });
    };

    onReplyChanged = (id, e) => {
        this.setState({
            ...this.state,
            replies: [
                ...this.state.replies.map(reply => {
                    if (reply.id !== id) {
                        return reply;
                    }

                    return {
                        ...reply,
                        message: e.currentTarget.value
                    };
                })
            ]
        }, () => {
            this.runValidation();
        });
    };

    onClose = (e) => {
        e.preventDefault();

        this.setState(defaultState(this.props));
        this.props.onClose();
    }

    onNewReply = (position) => {

        const newMessage = {
            'id': uuidv4(),
            'post_id': this.props.post.id,
            'message': "",
            'media': [],
            'position': position,
            'created_at': new Date().toISOString(),
            'updated_at': new Date().toISOString(),
        };

        const replies = [
            ...this.state.replies,
            newMessage
        ];

        // Shift the deleted one to the end - quick way to adjust all other positions.
        const reorderedReplies = PositionManager.move(replies, newMessage.id, newMessage.position);

        this.setState({
            ...this.state,
            replies: reorderedReplies,
            autoFocus: newMessage.id
        });

        // todo - add a new reply to all the messages...
        // todo - change position for all the things..
        // todo - make sure positions are persisted when saved.
        // todo - ensure replies are rendered in positioned order.
        // todo - maybe make a JS position manager, with tests?
    };

    onMoveUp = (replyId) => {

        const reply = this.state.replies.find(reply => reply.id === replyId);

        this.setState({
            ...this.state,
            replies: PositionManager.move(this.state.replies, replyId, reply.position - 1),
            autoFocus: reply.id
        });
    };

    onMoveDown = (replyId) => {

        const reply = this.state.replies.find(reply => reply.id === replyId);

        this.setState({
            ...this.state,
            replies: PositionManager.move(this.state.replies, replyId, reply.position + 1),
            autoFocus: reply.id
        });
    };

    onRemovePostMedia = (mediaId) => {
        const post = { ...this.state.post };
        post.media = post.media.filter(item => item !== mediaId);

        this.setState({
            ...this.state,
            post: post
        });
    };

    onRemoveReplyMedia = (replyId, mediaId) => {
        this.setState({
            ...this.state,
            replies: this.state.replies.map(reply => {
                if (reply.id !== replyId) {
                    return reply;
                }

                return {
                    ...reply,
                    media: reply.media.filter(item => item !== mediaId)
                };
            })
        });
    };

    onAddPostMedia = (mediaId) => {
        const post = { ...this.state.post };
        post.media.push(mediaId);

        this.setState({
            ...this.state,
            post: post
        });
    };

    onAddReplyMedia = (replyId, mediaId) => {
        this.setState({
            ...this.state,
            replies: this.state.replies.map(reply => {
                if (reply.id !== replyId) {
                    return reply;
                }

                return {
                    ...reply,
                    media: [
                        ...reply.media,
                        mediaId
                    ]
                };
            })
        });
    };

    render()
    {

        const { open } = this.props;
        const { post, replies } = this.state;

        const postValidation = this.runValidation(this.state.post);

        const sortedReplies = _.sortBy(replies, 'position');

        return <Modal open={open} onClose={this.onClose} className='w-full max-w-xl'>
            <Modal.Header>
                Edit Twitter Thread
            </Modal.Header>
            <Modal.Content>
                <ErrorBoundary>
                    <form id='post-editor' onSubmit={this.onSubmit}>
                        <fieldset className='space-y-4'>
                            <div className="flex flex-col">
                                <Post
                                    message={post.message}
                                    onChange={this.onMessageChanged}
                                    error={postValidation.hasErrors}
                                    help={postValidation.help}
                                    media={post.media.map(uuid => this.props.media[uuid])}
                                    addMedia={this.onAddPostMedia}
                                    removeMedia={this.onRemovePostMedia}
                                    tray={[
                                        <TrayButton
                                            icon={PlusIcon}
                                            onClick={() => this.onNewReply(1)}
                                            disabled={! post.message}
                                        />
                                    ]}
                                />
                            </div>
                            {
                                sortedReplies.length > 0 &&
                                    <Feed>
                                        {
                                            sortedReplies.map(reply => {
                                                const media = reply.media.map(uuid => this.props.media[uuid]);
                                                const replyValidation = this.runValidation({
                                                    ...reply,
                                                    media: media
                                                });

                                                const tray = [
                                                    <TrayButton
                                                        key='plus'
                                                        icon={PlusIcon}
                                                        onClick={() => this.onNewReply(reply.position + 1)}
                                                        disabled={! reply.message}
                                                    />,
                                                    <TrayButton
                                                        key='trash'
                                                        icon={TrashIcon}
                                                        onClick={() => this.onDelete(reply.id)}
                                                    />
                                                ];

                                                if (reply.position < sortedReplies.length) {
                                                    tray.push(
                                                        <TrayButton
                                                            key='down'
                                                            icon={ArrowDownIcon}
                                                            onClick={() => this.onMoveDown(reply.id)}
                                                        />
                                                    );
                                                }

                                                if (reply.position > 1) {
                                                    tray.push(
                                                        <TrayButton
                                                            key='up'
                                                            icon={ArrowUpIcon}
                                                            onClick={() => this.onMoveUp(reply.id)}
                                                        />
                                                    );
                                                }

                                                // tray.push(
                                                //     <Badge>{reply.position}</Badge>
                                                // );

                                                return <FeedItem key={reply.id} type='reply'>
                                                    <Post
                                                        autoFocus={this.state.autoFocus === reply.id}
                                                        message={reply.message}
                                                        onChange={e => this.onReplyChanged(reply.id, e)}
                                                        error={replyValidation.hasErrors}
                                                        help={replyValidation.help}
                                                        media={media}
                                                        addMedia={(mediaId) => this.onAddReplyMedia(reply.id, mediaId)}
                                                        removeMedia={(mediaId) => this.onRemoveReplyMedia(reply.id, mediaId)}
                                                        tray={tray}
                                                    />
                                                </FeedItem>
                                            })
                                        }
                                    </Feed>
                            }
                        </fieldset>
                    </form>
                </ErrorBoundary>
            </Modal.Content>
            <Modal.Footer>
                <Button
                    form='post-editor'
                    type={this.state.validation.hasErrors ? 'secondary' : 'primary'}
                    busy={this.state.busy}
                    disabled={this.state.validation.hasErrors}
                >
                    Update
                </Button>
                <Button onClick={this.onClose}>Cancel</Button>
            </Modal.Footer>
        </Modal>;
    }
}

const Post = ({message, onChange, media, addMedia, removeMedia, ...props}) => (
    <div className='flex-1 space-y-4'>
        <TextArea
            id="message"
            value={message}
            onChange={onChange}
            {...props}
        />

        <AttachedImages
            limit={4}
            media={media}
            onAdd={addMedia}
            onRemove={removeMedia}
        />
    </div>
);
